import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";

import Ripple from "components/ripple";
import OutsideAlerter from "components/outsideAlerter";
import { isDesktopBelow, isDesktop } from "utils/deviceType";
import { generateUrl } from "utils/generateUrl";
import { trackMOEEvent } from "utils/analytics";
import { navigateHandler } from "utils/redirection";
import { isGuestUser } from "utils/guestUtils";

import { faOnNeedHelpClick, faOnServicesClick } from "./services/analytics.services";
import { getServiceConfig } from "./utils/utils";
import styles from "./postLoginSidebar.scss";
import { BASE_ASSETS_URL } from "constants/app";
import Environment from "services/environment";
import { LOB_INVENTORY_ROUTE_MAP, LOB_URL_MAP, LIB_CONSUMER, LINK_TO_ROUTE, LOB } from "constants/common";
import { MOE_SIDEBAR, NOT_PERSIST_PARTYID_MENU } from "./constants/constants";
import Tooltip, { TOOLTIP_POSITIONS } from "components/tooltip";
import { requirePartyId } from "utils/partyIdUtils";
import HelpCard from "./helpCard/helpCard";

const env = Environment.getEnv();

const {
  new_humburger_menu,
  new_humburger_icon,
  have_sub_menu,
  active,
  nav_ripple,
  link_icon,
  link_text,
  arrow_icon,
  beta_tag,
  up,
  down,
  navbar_list,
  navigation_conent,
  sidebar,
  sidebar_menu,
  sidebar_expanded,
  dropback,
  hamburger,
  hamburger_inner,
  product_type,
} = styles;

const SidebarHeader = ({ isSidebarExpanded, handleIsSidebarExpanded }) => {
  return (
    <div className={new_humburger_menu}>
      <Ripple>
        <span className={new_humburger_icon} onClick={() => handleIsSidebarExpanded(!isSidebarExpanded)}>
          <img src={`${BASE_ASSETS_URL}images/hamburger.svg`} alt="Expand" width={"17px"} height={"14px"} />
        </span>
      </Ripple>
    </div>
  );
};

const getCurrentService = (location, serviceConfig) => {
  let selectedService = [];
  serviceConfig.forEach((service) => {
    if (service.pathRegex?.some((path) => location.pathname.includes(path))) {
      selectedService.push(service.code);
      if (service.subMenu) {
        selectedService.push(...getCurrentService(location, service.subMenu));
      }
    }
  });
  return selectedService;
};

const getProductsProcessedSubMenu = (lobs, partnerProducts) => {
  let subMenu = [];

  const airtelProductsSubMenu = lobs
    ? []
    : [
        {
          code: null,
          name: "No Active Products",
          url: null,
          pathRegex: [],
          show: true,
        },
      ];

  lobs?.forEach(({ key: lob, displayName: lobDisplayName, products }) => {
    const lobSubMenu = [
      {
        code: `all_${LOB_URL_MAP[lob]}`,
        name: "All",
        lob,
        url: generateUrl(LINK_TO_ROUTE.PRODUCT_DASHBOARD, { lob }),
        pathRegex: [`/${LOB_URL_MAP[lob]}/${LOB_INVENTORY_ROUTE_MAP[lob]}`],
        show: true,
        isPartyIdSupported: true,
      },
    ];

    products.forEach(({ productType, productDisplayName }) => {
      lobSubMenu.push({
        code: productType,
        name: productDisplayName,
        lobDisplayName,
        lob,
        showTooltip: true,
        url: generateUrl(LINK_TO_ROUTE.PRODUCT_DASHBOARD, { lob, product: productType }),
        pathRegex: [`/${LOB_URL_MAP[lob]}/${productType}/${LOB_INVENTORY_ROUTE_MAP[lob]}`],
        show: true,
        isPartyIdSupported: true,
      });
    });
    if (lobSubMenu.length > 0) {
      airtelProductsSubMenu.push({
        code: lob,
        name: lobDisplayName,
        pathRegex: lob === LOB.AIRTELIQ ? [`/${LOB_URL_MAP[lob]}/${LOB_INVENTORY_ROUTE_MAP[lob]}`] : [`/${LOB_URL_MAP[lob]}`],
        show: true,
        subMenu: lob === LOB.AIRTELIQ ? [] : lobSubMenu,
        isPartyIdSupported: true,
      });
    }
  });

  subMenu =
    airtelProductsSubMenu.length > 0
      ? [
          ...subMenu,
          {
            code: "from_airtel",
            name: "FROM AIRTEL",
            show: true,
            pathRegex: [],
            subMenu: [],
            style: product_type,
            isPartyIdSupported: true,
          },
          ...airtelProductsSubMenu,
          {
            code: "caller_name_display",
            name: "Business Name Display",
            url: env.callerNameDisplayUrl,
            pathRegex: ["/caller-name"],
            show: true,
            isExternalLink: true,
            isPartyIdSupported: false,
          },
        ]
      : subMenu;

  const partnerProductsSubMenu = partnerProducts
    ? partnerProducts?.map(({ id, title, urlKey }) => {
        return {
          code: id,
          name: title,
          url: env[urlKey],
          pathRegex: [`/${id}`],
          show: true,
          isExternalLink: true,
          isPartyIdSupported: true,
        };
      })
    : [];

  subMenu =
    partnerProductsSubMenu.length > 0
      ? [
          ...subMenu,
          {
            code: "from_airtel_partners",
            name: "FROM AIRTEL PARTNERS ",
            show: true,
            isExternalLink: true,
            pathRegex: [],
            subMenu: [],
            style: product_type,
            isPartyIdSupported: true,
          },
          ...partnerProductsSubMenu,
        ]
      : subMenu;

  return subMenu;
};

export default function PostLoginSidebar({
  source = LIB_CONSUMER.NOT_ATB,
  lobs,
  partnerProducts,
  lobDetails,
  partyDetails,
  isInternalUser,
}) {
  const subMenuConfig = {
    products: getProductsProcessedSubMenu(lobs, partnerProducts),
  };
  const history = useHistory();
  const location = useLocation();
  const serviceConfig = getServiceConfig({ isInternalUser, isGuestUser: isGuestUser() });

  const [isExpanded, setIsExpanded] = useState([]);
  const [scrollSideNavHeight, setScrollSideNavHeight] = useState(0);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [processedServiceConfig, setProcessedServiceConfig] = useState(serviceConfig);

  useEffect(() => {
    const mergedServiceConfig = serviceConfig.map((service) => {
      const { code, subMenu } = service;
      return {
        ...service,
        subMenu: subMenuConfig[code] ? subMenuConfig[code] : subMenu,
      };
    });
    const currentService = getCurrentService(location, mergedServiceConfig);
    setIsExpanded(currentService);
    setProcessedServiceConfig(mergedServiceConfig);
  }, [lobs, partnerProducts, location.pathname]);

  useEffect(() => {
    document.getElementsByClassName("b2b-lib-navigation_conent")[0].scrollTo(0, scrollSideNavHeight);
  }, [isExpanded]);

  const isDesktopBelowView = isDesktopBelow();
  const isDesktopView = isDesktop();

  const handleIsExpanded = (serviceCode) => {
    if (isExpanded.includes(serviceCode)) {
      const index = isExpanded.indexOf(serviceCode);
      if (index > -1) {
        isExpanded.splice(index, 1);
      }
      setIsExpanded([...isExpanded]);
    } else {
      setIsExpanded([...isExpanded, serviceCode]);
    }
  };

  const handleIsSidebarExpanded = (value) => {
    value ? (document.body.style.overflowY = "hidden") : (document.body.style.overflowY = "auto");
    setIsSidebarExpanded(value);
  };

  const handleNavigation = (path, serviceCode, isExternalLink, isPartyIdSupported, selectedLob) => {
    if (isExternalLink) {
      window.location.href = path;
      return;
    }
    let _partyDetails = partyDetails;
    let _lobDetails = lobDetails;
    if (
      (NOT_PERSIST_PARTYID_MENU.includes(serviceCode) && requirePartyId(lobDetails?.key) && !_partyDetails?.partyId) ||
      !isPartyIdSupported
    ) {
      _partyDetails = null;
      _lobDetails = null;
    }
    if (selectedLob && _lobDetails?.key && _lobDetails.key !== selectedLob) {
      // detect lob change
      _partyDetails = null;
    }
    const url = generateUrl(path, { lob: _lobDetails?.key }, { partyId: _partyDetails?.partyId });
    navigateHandler({ history, source, path: url });
  };

  const onServicesClick = (e, service, serviceCode, subMenu, url, isExternalLink, isPartyIdSupported, selectedLob) => {
    e.preventDefault();
    e.stopPropagation();
    setScrollSideNavHeight(document.getElementsByClassName("b2b-lib-navigation_conent")[0].scrollTop); // setting scroll height to current position
    if (subMenu?.length > 0) {
      handleIsExpanded(serviceCode);
    }
    if (serviceCode === LOB.AIRTELIQ) {
      url = generateUrl(LINK_TO_ROUTE.PRODUCT_DASHBOARD, { lob: serviceCode });
    }
    if (url) {
      faOnServicesClick({ name: service });
      trackMOEEvent(`${MOE_SIDEBAR}_SERVICE_CLICK`, { service }).finally(() => {
        setIsSidebarExpanded(false);
        handleNavigation(url, serviceCode, isExternalLink, isPartyIdSupported, selectedLob);
      });
    }
  };

  const onNeedhelpClick = () => {
    faOnNeedHelpClick();
    setIsSidebarExpanded(false);
    const url = generateUrl(LINK_TO_ROUTE.ACCOUNT_CONTACTS, { lob: lobDetails?.key }, { partyId: partyDetails?.partyId });
    navigateHandler({ history, source, path: url });
  };

  const SidebarServices = ({ services }) => {
    return services.map(
      ({
        code: serviceCode,
        name,
        showTooltip,
        iconClass,
        subMenu,
        url,
        style,
        isExternalLink,
        isPartyIdSupported,
        lob,
        isInBetaPhase,
      }) => {
        const isSelected = isExpanded.includes(serviceCode);

        return (
          <li
            key={serviceCode}
            className={`${subMenu?.length > 0 ? have_sub_menu : ""} ${style ? style : ""} ${
              isExpanded.includes(serviceCode) ? active : ""
            }`}
          >
            <div className={nav_ripple}>
              {showTooltip && name.length > 18 ? (
                <Tooltip
                  position={TOOLTIP_POSITIONS.BOTTOM_END}
                  content={name}
                  parent={
                    <a
                      role="button"
                      onClick={(e) =>
                        onServicesClick(e, name, serviceCode, subMenu, url, isExternalLink, isPartyIdSupported, lob)
                      }
                      target={""}
                      id={serviceCode}
                    >
                      <span className={link_icon}>
                        <i className={`aeh-icon-${iconClass}`} title={iconClass}></i>
                      </span>
                      <span className={link_text}>{name}</span>
                      {subMenu?.length > 0 && <i className={`${arrow_icon} ${isSelected ? up : down}`} />}
                      {isInBetaPhase && <span className={beta_tag}>Beta</span>}
                    </a>
                  }
                ></Tooltip>
              ) : (
                <a
                  role="button"
                  onClick={(e) => onServicesClick(e, name, serviceCode, subMenu, url, isExternalLink, isPartyIdSupported, lob)}
                  target={""}
                  id={serviceCode}
                >
                  <span className={link_icon}>
                    <i className={`aeh-icon-${iconClass}`} title={iconClass}></i>
                  </span>
                  <span className={link_text}>{name}</span>
                  {subMenu?.length > 0 && <i className={`${arrow_icon} ${isSelected ? up : down}`} />}
                  {isInBetaPhase && <span className={beta_tag}>Beta</span>}
                </a>
              )}
            </div>
            {subMenu?.length > 0 && isSelected && (
              <ul className={navbar_list}>
                <SidebarServices services={subMenu} />
              </ul>
            )}
          </li>
        );
      },
    );
  };

  const SidebarContents = () => {
    return (
      <>
        {isDesktopBelowView && (
          <SidebarHeader isSidebarExpanded={isSidebarExpanded} handleIsSidebarExpanded={handleIsSidebarExpanded} />
        )}
        <div className={navigation_conent}>
          <ul className={navbar_list}>
            <SidebarServices services={processedServiceConfig} />
          </ul>
          {!isInternalUser && <HelpCard onNeedhelpClick={onNeedhelpClick} />}
        </div>
      </>
    );
  };

  return (
    <OutsideAlerter onOutsideClick={() => handleIsSidebarExpanded(false)}>
      <div className={sidebar}>
        {isDesktopBelowView && (
          <div
            className={`${hamburger} ${isSidebarExpanded ? active : ""}`}
            onClick={() => handleIsSidebarExpanded(!isSidebarExpanded)}
          >
            <span className={hamburger_inner}></span>
          </div>
        )}
        <div className={`${isSidebarExpanded ? dropback : ""}`}>
          <OutsideAlerter onOutsideClick={() => handleIsSidebarExpanded(false)}>
            <nav className={`${sidebar_menu} ${isSidebarExpanded || isDesktopView ? sidebar_expanded : ""}`}>
              <SidebarContents />
            </nav>
          </OutsideAlerter>
        </div>
      </div>
    </OutsideAlerter>
  );
}
